import {
    collection,
    doc,
    getCountFromServer,
    getDoc,
    getDocs,
    limit as fbLimit,
    orderBy,
    query,
    where,
} from 'firebase/firestore';
import { db } from '../config/firebase';
import { toDate, type RankingEntry } from '@kaeliux/shared';
import { getSelectedUniverseId } from '../store/universeStore';

/** Top-100 leaderboard cap (issue #22). */
export const TOP_RANKINGS_LIMIT = 100;

function normalizeEntry(userId: string, data: Record<string, unknown>): RankingEntry {
    const total = (data.total as number) ?? 0;
    return {
        userId,
        displayName: (data.displayName as string) || 'Unknown Commander',
        race: data.race as RankingEntry['race'],
        buildingPoints: (data.buildingPoints as number) ?? 0,
        shipPoints: (data.shipPoints as number) ?? 0,
        shipUpgradePoints: (data.shipUpgradePoints as number) ?? 0,
        sciencePoints: (data.sciencePoints as number) ?? 0,
        // Fall back to `total` for docs written before the resource/battle split existed.
        resourcePoints: (data.resourcePoints as number) ?? total,
        battlePoints: (data.battlePoints as number) ?? 0,
        total,
        photoURL: data.photoURL as string | undefined,
        mainPlanetId: data.mainPlanetId as string | undefined,
        mainPlanetCoordinates: data.mainPlanetCoordinates as RankingEntry['mainPlanetCoordinates'],
        // Written by the alliance endpoints on join/leave, never by
        // `commitAdvance` (which writes this doc with `merge: true` on every
        // tick and so leaves them alone). Absent for an unaffiliated player.
        allianceId: data.allianceId as string | undefined,
        allianceTag: data.allianceTag as string | undefined,
        allianceName: data.allianceName as string | undefined,
        updatedAt: toDate(data.updatedAt, new Date(0)),
    };
}

/**
 * Top N players by total points, descending — `universes/{u}/rankings/{uid}`
 * is written server-side on every commit (see `commitAdvance` in
 * `apps/functions/src/advance.ts`) and is publicly readable by any
 * authenticated user within that universe (`firestore.rules`), so this is a
 * direct client query rather than a Cloud Function call. Scoped per-universe
 * so players in different universes are never ranked against each other.
 */
export async function getTopRankings(limitCount: number = TOP_RANKINGS_LIMIT): Promise<RankingEntry[]> {
    const q = query(collection(db, 'universes', getSelectedUniverseId(), 'rankings'), orderBy('total', 'desc'), fbLimit(limitCount));
    const snap = await getDocs(q);
    return snap.docs.map((d) => normalizeEntry(d.id, d.data()));
}

/** A single player's own leaderboard row, or `null` if they have no ranked points yet. */
export async function getPlayerRankingEntry(userId: string): Promise<RankingEntry | null> {
    const snap = await getDoc(doc(db, 'universes', getSelectedUniverseId(), 'rankings', userId));
    if (!snap.exists()) return null;
    return normalizeEntry(snap.id, snap.data());
}

/**
 * 1-based rank among all ranked players in this universe: the count of
 * players with strictly more total points, plus one. Uses a server-side
 * count aggregation query so a player far outside the visible top 100
 * doesn't require fetching every row just to learn their own rank.
 */
export async function getPlayerRank(totalPoints: number): Promise<number> {
    const q = query(collection(db, 'universes', getSelectedUniverseId(), 'rankings'), where('total', '>', totalPoints));
    const snap = await getCountFromServer(q);
    return snap.data().count + 1;
}
